<div class="h-full min-h-0 overflow-y-auto scrollbar-transparent">
  <div class="flex flex-col gap-4 pb-2">
    @if (formMode()) {
      <ins-creator-payment-details-form
        [mode]="formMode()!"
        [beneficiary]="beneficiaryForForm()"
        [hasExisting]="hasExisting()"
        [creatorId]="creatorUserId()!"
        (saved)="onFormSaved()"
        (cancelled)="closeForm()" />
    } @else {
      @switch (viewState().kind) {
        @case ('loading') {
          <p class="text-sm text-secondary m-0">Loading payment details…</p>
        }
        @case ('error') {
          <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
            <p class="m-0 text-sm text-red-600" role="alert">{{ errorState()?.message }}</p>
            @if (errorState()?.canRetry) {
              <button type="button" class="primary-button mt-4" (click)="loadPaymentDetails()">Try again</button>
            }
          </div>
        }
        @case ('none') {
          <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
            <p class="m-0 text-sm font-medium text-primary">No bank details on file</p>
            <p class="m-0 mt-1 text-sm text-secondary">
              Add bank details for this creator. We support UK, US, Irish and euro-area bank accounts.
            </p>
            <button type="button" class="primary-button mt-4" (click)="openAddForm()">Add bank details</button>
          </div>
        }
        @case ('unavailable') {
          <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
            <p class="m-0 text-sm font-medium text-primary">Bank details are saved</p>
            <p class="m-0 mt-1 text-sm text-secondary">
              This creator has bank details on file, but we couldn't load them from Airwallex right now. Please try
              again.
            </p>
            <button type="button" class="primary-button mt-4" (click)="loadPaymentDetails()">Try again</button>
          </div>
        }
        @case ('available') {
          <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
            <p class="m-0 text-sm font-medium text-emerald-700 dark:text-emerald-300">Bank details on file</p>
            <p class="m-0 mt-1 text-sm text-secondary">Sensitive values are partially masked.</p>
            <button type="button" class="secondary-button mt-4" (click)="openEditForm()">Edit details</button>
          </div>

          @let details = availableState()!.beneficiary;
          @let beneficiary = details.beneficiary;
          @let address = beneficiary.address;
          @let bank = beneficiary.bank_details;

          <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950 space-y-6">
            <section>
              <h4 class="text-sm font-semibold text-primary m-0">Account</h4>
              <dl class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 m-0">
                <div>
                  <dt class="text-sm text-secondary m-0">Beneficiary ID</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ obfuscateSensitiveValue(details.id) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Payer entity type</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(details.payer_entity_type) }}</dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="text-sm text-secondary m-0">Transfer methods</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ formatTransferMethods(details.transfer_methods) }}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h4 class="text-sm font-semibold text-primary m-0">Beneficiary</h4>
              <dl class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 m-0">
                <div>
                  <dt class="text-sm text-secondary m-0">Entity type</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(beneficiary.entity_type) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Account type</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(beneficiary.type) }}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h4 class="text-sm font-semibold text-primary m-0">Address</h4>
              <dl class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 m-0">
                <div class="sm:col-span-2">
                  <dt class="text-sm text-secondary m-0">Street address</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(address?.street_address) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">City</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(address?.city) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">State</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(address?.state) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Postcode</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(address?.postcode) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Country code</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(address?.country_code) }}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h4 class="text-sm font-semibold text-primary m-0">Bank details</h4>
              <dl class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 m-0">
                <div>
                  <dt class="text-sm text-secondary m-0">Account name</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(bank.account_name) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Account currency</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(bank.account_currency) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Bank country code</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(bank.bank_country_code) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Bank name</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(bank.bank_name) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">IBAN</dt>
                  <dd class="m-0 mt-1 text-sm font-mono text-primary">{{ obfuscateSensitiveValue(bank.iban) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Account number</dt>
                  <dd class="m-0 mt-1 text-sm font-mono text-primary">
                    {{ obfuscateSensitiveValue(bank.account_number) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Routing type</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(bank.account_routing_type1) }}</dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Routing value 1</dt>
                  <dd class="m-0 mt-1 text-sm font-mono text-primary">
                    {{ obfuscateSensitiveValue(bank.account_routing_value1) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Routing value 2</dt>
                  <dd class="m-0 mt-1 text-sm font-mono text-primary">
                    {{ obfuscateSensitiveValue(bank.account_routing_value2) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-sm text-secondary m-0">Account category</dt>
                  <dd class="m-0 mt-1 text-sm text-primary">{{ displayValue(bank.bank_account_category) }}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h4 class="text-sm font-semibold text-primary m-0">Additional info</h4>
              <dl class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 m-0">
                <div class="sm:col-span-2">
                  <dt class="text-sm text-secondary m-0">External identifier</dt>
                  <dd class="m-0 mt-1 text-sm font-mono text-primary">
                    {{ obfuscateSensitiveValue(beneficiary.additional_info?.external_identifier) }}
                  </dd>
                </div>
              </dl>
            </section>
          </div>
        }
      }
    }
  </div>
</div>
